import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, IconButton, InputAdornment, MenuItem, Select, SelectChangeEvent, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Collapse, SvgIcon, useTheme } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Section } from '../../ui/Section';
import { Pill } from '../Pill';
import CustomPagination from '../../forms/Pagination';
import LoadingWheel from '../../ui/LoadingWheel';
import { UserContext } from '../useUser';
import { Roles } from '../../../enums/Role';
import { ChangeLogTarget } from '../../../enums/ChangeLogTarget';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { trainingClient } from '../../../app/services/api/compliance/training';
import { SearchSm } from '../../../assets';

const ROWS_PER_PAGE = 10;
const ALL_ACTIONS = '-1';

type ChangeLogAction = 'Insert' | 'Update' | 'Delete';

// Shape shared by the dbo/Audit/Training ChangeLog responses (nullability of optional fields differs slightly per source).
export interface ChangeLogEntryRow {
  id: number;
  entityName: string;
  entityId: string;
  action: string;
  timestamp: string;
  userId: string | null;
  userName: string | null;
  changes: { propertyName?: string | null; before?: string | null; after?: string | null }[];
}

export interface ChangeLogPageRequest {
  action?: ChangeLogAction;
  search?: string;
  skip: number;
  take: number;
}

export type ChangeLogFetcher = (request: ChangeLogPageRequest) => Promise<{ data: ChangeLogEntryRow[]; totalCount: number }>;

type AuditHistorySource =
  | {
    /** The ChangeLog target to query (see the API's ChangeLogTarget/AuditEntityRegistry). */
    target: ChangeLogTarget;
    /** Optional entity ID to scope the history to a single record. */
    entityId?: string;
    fetchEntries?: never;
  }
  | {
    /**
     * For a subject whose history is served by its own feature endpoint (which authorises the subject
     * itself) instead of the shared ChangeLog target registry. Give the panel a `key` per subject.
     */
    fetchEntries: ChangeLogFetcher;
    target?: never;
    entityId?: never;
  };

export type AuditHistoryPanelProps = {
  /**
   * Permission held for the entity being audited (e.g. "ViewUserAudit"). Used only for
   * client-side visibility: the panel renders nothing if the current user lacks this permission.
   * Access is re-checked server-side against the resolved `target`, not this value.
   */
  permission: Roles;
  /** Optional panel title, defaults to a generic translated title. */
  title?: string;
} & AuditHistorySource;

/**
 * Reusable, entity-agnostic audit history panel. Fetches paginated, searchable, reverse-chronological
 * change history from the ChangeLog API and renders before/after values per changed property.
 * Routes to /Audit/ChangeLog or /Training/ChangeLog for those targets, dbo /ChangeLog otherwise.
 *
 * Drop this into any page for any audited entity, e.g.:
 *   <AuditHistoryPanel permission="ViewUserAudit" target={ChangeLogTarget.User} entityId={String(user.id)} />
 */
export const AuditHistoryPanel: React.FC<AuditHistoryPanelProps> = ({ permission, target, entityId, fetchEntries, title }) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { hasPermissionTo } = useContext(UserContext);
  const canView = hasPermissionTo([permission]);
  const { searchChangeLog } = useNswagClient();

  const [entries, setEntries] = useState<ChangeLogEntryRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [actionFilter, setActionFilter] = useState(ALL_ACTIONS);
  const [appliedAction, setAppliedAction] = useState(ALL_ACTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const fetchEntriesRef = useRef(fetchEntries);
  fetchEntriesRef.current = fetchEntries;

  useEffect(() => {
    if (!canView) return;

    let cancelled = false;
    setIsLoading(true);

    const actionParam = appliedAction !== ALL_ACTIONS ? appliedAction as ChangeLogAction : undefined;
    const skip = page * ROWS_PER_PAGE;

    const fetchChangeLog = async (): Promise<{ data: ChangeLogEntryRow[]; totalCount: number }> => {
      if (fetchEntriesRef.current) {
        return fetchEntriesRef.current({ action: actionParam, search: appliedSearch || undefined, skip, take: ROWS_PER_PAGE });
      }

      if (target === ChangeLogTarget.Audit) {
        const result = await complianceService.changeLog.search(
          target, entityId, actionParam, appliedSearch || undefined, skip, ROWS_PER_PAGE,
        );
        return { data: result.data, totalCount: result.totalCount };
      }

      if (target === ChangeLogTarget.Training) {
        const result = await trainingClient.searchChangeLog(
          target, entityId, actionParam, appliedSearch || undefined, skip, ROWS_PER_PAGE,
        );
        return { data: result.data, totalCount: result.totalCount };
      }

      const response = await searchChangeLog(
        target as ChangeLogTarget, entityId, actionParam, appliedSearch || undefined, skip, ROWS_PER_PAGE,
      );
      return { data: response.data?.data ?? [], totalCount: response.data?.totalCount ?? 0 };
    };

    fetchChangeLog()
      .then(({ data, totalCount }) => {
        if (cancelled) return;
        setEntries(data);
        setTotalCount(totalCount);
      })
      .catch((err) => {
        if (cancelled) return;
        setEntries([]);
        setTotalCount(0);
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, target, entityId, appliedSearch, appliedAction, page]);

  if (!canView) {
    return null;
  }

  const handleSearch = () => {
    setPage(0);
    setAppliedSearch(searchTerm);
    setAppliedAction(actionFilter);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
    setAppliedSearch('');
  };

  const handleActionFilterChange = (event: SelectChangeEvent) => {
    setActionFilter(event.target.value);
  };

  const maxPages = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));

  const getActionColors = (action: string) => {
    switch (action) {
      case 'Insert':
        return {
          background: theme.palette.success[50],
          border: theme.palette.success[200],
          text: theme.palette.success[700],
        };
      case 'Update':
        return {
          background: theme.palette.custom.blue[50],
          border: theme.palette.custom.blue[200],
          text: theme.palette.custom.blue[700],
        };
      case 'Delete':
        return {
          background: theme.palette.error[50],
          border: theme.palette.error[200],
          text: theme.palette.error[700],
        };
      default:
        return {
          background: theme.palette.custom.gray[50],
          border: theme.palette.custom.gray[200],
          text: theme.palette.custom.gray[700],
        };
    }
  };

  return (
    <Section>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ display: 'flex', alignItems: 'center' }}>
          {title ?? t('auditHistory.title')}
          <Pill
            text={`${totalCount} ${
              totalCount === 1
                ? t('auditHistory.totalRecord')
                : t('auditHistory.totalRecords')
            }`}
          />
        </Typography>
        <Stack direction="row"
          spacing={2}
          alignItems="center">
          <TextField
            size="small"
            label={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            InputProps={{
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small"
                    aria-label={t('clearFilters')}
                    onClick={handleClearSearch}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 0 }}
          />
          <Select
            size="small"
            value={actionFilter}
            onChange={handleActionFilterChange}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value={ALL_ACTIONS}>{t('auditHistory.allActions')}</MenuItem>
            <MenuItem value="Insert">Insert</MenuItem>
            <MenuItem value="Update">Update</MenuItem>
            <MenuItem value="Delete">Delete</MenuItem>
          </Select>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSearch}
            startIcon={<SvgIcon><SearchSm /></SvgIcon>}
          >
            {t('search')}
          </Button>
        </Stack>
      </Box>

      <TableContainer>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <LoadingWheel />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('auditHistory.entityId')}</TableCell>
                <TableCell>{t('auditHistory.entityName')}</TableCell>
                <TableCell>{t('auditHistory.action')}</TableCell>
                <TableCell>{t('auditHistory.user')}</TableCell>
                <TableCell>{t('auditHistory.timestamp')}</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="textSM"
                      sx={{ textAlign: 'center', display: 'block', py: 4 }}>
                      {t('noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => {
                  const isExpanded = expandedId === entry.id;
                  const actionColors = getActionColors(entry.action);
                  return (
                    <React.Fragment key={entry.id}>
                      <TableRow
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      >
                        <TableCell>
                          <Typography variant="textXS">{entry.entityId}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textXS">{entry.entityName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.action}
                            size="small"
                            sx={{
                              backgroundColor: actionColors.background,
                              border: `1px solid ${actionColors.border}`,
                              color: actionColors.text,
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              height: '24px',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="textXS">{entry.userName ?? entry.userId ?? '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textXS">{new Date(entry.timestamp).toLocaleString()}</Typography>
                        </TableCell>

                        <TableCell>
                          <IconButton size="small"
                            aria-label={isExpanded ? t('auditHistory.collapse') : t('auditHistory.expand')}>
                            <SvgIcon fontSize="small">{isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</SvgIcon>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6}
                            sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded}
                              timeout={200}
                              unmountOnExit>
                              <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t('auditHistory.field')}</TableCell>
                                      <TableCell>{t('auditHistory.before')}</TableCell>
                                      <TableCell>{t('auditHistory.after')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {entry.changes.map((change, idx) => (
                                      <TableRow key={`${entry.id}-${change.propertyName ?? idx}`}>
                                        <TableCell>{change.propertyName}</TableCell>
                                        <TableCell>{change.before ?? '-'}</TableCell>
                                        <TableCell>{change.after ?? '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {totalCount > 0 && (
        <CustomPagination
          page={page}
          setPage={setPage}
          maxPages={maxPages}
          breakpointForChangeDisplay={120000}
          disabled={isLoading}
        />
      )}
    </Section>
  );
};

export default AuditHistoryPanel;
